/**
 * 实体自动完成 Hook
 * 处理 @ 字符触发的项目/任务自动完成功能
 */
import { useCallback, useMemo, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { entityAutocompleteAtom, chatInputAtom } from '../../atoms/v3/chatInputAtoms';
import { projectsAtom } from '../../atoms/v0/projectAtoms';
import { tasksAtom } from '../../atoms/v0/taskAtoms';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
// NOTE: pinyin-match 库的正确用法：import PinyinMatch from 'pinyin-match' (默认导入)，然后使用 PinyinMatch.match(text, keyword)
import PinyinMatch from 'pinyin-match';

// 混合实体类型
export interface MixedEntity {
  id: string;
  title: string;
  type: 'project' | 'task';
  data: Project | Task;
}

// 搜索结果类型
export interface SearchResult {
  projects: MixedEntity[];
  tasks: MixedEntity[];
  all: MixedEntity[];
}

/**
 * 实体自动完成 Hook
 */
export const useEntityAutocomplete = () => {
  const [autocompleteState, setAutocompleteState] = useAtom(entityAutocompleteAtom);
  const [chatInput, setChatInput] = useAtom(chatInputAtom);
  const allProjects = useAtomValue(projectsAtom);
  const allTasks = useAtomValue(tasksAtom);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 转换项目为混合实体
  const projectEntities = useMemo((): MixedEntity[] => {
    return allProjects.map(project => ({
      id: project.id,
      title: project.title,
      type: 'project' as const,
      data: project
    }));
  }, [allProjects]);

  // 转换任务为混合实体（过滤掉已完成的任务）
  const taskEntities = useMemo((): MixedEntity[] => {
    return allTasks
      .filter(task => !task.done) // 过滤掉已完成的任务
      .map(task => ({
        id: task.id,
        title: task.title,
        type: 'task' as const,
        data: task
      }));
  }, [allTasks]);

  // 搜索实体（支持拼音搜索）
  const searchEntities = useCallback((query: string): SearchResult => {
    if (!query.trim()) {
      // 如果没有查询词，返回所有实体（限制数量避免性能问题）
      const limitedProjects = projectEntities.slice(0, 10);
      const limitedTasks = taskEntities.slice(0, 20);
      const all = [...limitedProjects, ...limitedTasks];

      return {
        projects: limitedProjects,
        tasks: limitedTasks,
        all
      };
    }

    const lowerQuery = query.toLowerCase();

    // 项目搜索（支持拼音）
    const filteredProjects = projectEntities.filter(entity => {
      const title = entity.title;
      // 普通文本匹配
      if (title.toLowerCase().includes(lowerQuery)) {
        return true;
      }
      // 拼音匹配（全拼和首字母）
      // NOTE: 使用 PinyinMatch.match(text, keyword)，返回匹配位置数组或 false
      try {
        return PinyinMatch.match(title, query);
      } catch {
        return false;
      }
    });

    // 任务搜索（支持拼音）
    const filteredTasks = taskEntities.filter(entity => {
      const title = entity.title;
      // 普通文本匹配
      if (title.toLowerCase().includes(lowerQuery)) {
        return true;
      }
      // 拼音匹配（全拼和首字母）
      // NOTE: 使用 PinyinMatch.match(text, keyword)，返回匹配位置数组或 false
      try {
        return PinyinMatch.match(title, query);
      } catch {
        return false;
      }
    });

    // 合并并按类型排序（项目在前，任务在后）
    const all = [...filteredProjects, ...filteredTasks];

    return {
      projects: filteredProjects,
      tasks: filteredTasks,
      all
    };
  }, [projectEntities, taskEntities]);

  // 检测 @ 字符触发
  const detectAtTrigger = useCallback((value: string, cursorPosition: number) => {
    // 查找光标前最近的 @ 字符
    let atPosition = -1;
    for (let i = cursorPosition - 1; i >= 0; i--) {
      if (value[i] === '@') {
        atPosition = i;
        break;
      }
      // 如果遇到空格或换行，停止搜索
      if (value[i] === ' ' || value[i] === '\n') {
        break;
      }
    }

    if (atPosition === -1) {
      return null;
    }

    // 检查 @ 前是否为空格、换行或字符串开始
    const charBeforeAt = atPosition > 0 ? value[atPosition - 1] : ' ';
    if (charBeforeAt !== ' ' && charBeforeAt !== '\n') {
      return null;
    }

    // 提取 @ 后的查询字符串
    const queryStart = atPosition + 1;
    const queryEnd = cursorPosition;
    const query = value.slice(queryStart, queryEnd);

    // 检查查询字符串是否只包含字母、数字、中文和空格
    if (!/^[\w\u4e00-\u9fa5\s]*$/.test(query)) {
      return null;
    }

    return {
      triggerPosition: atPosition,
      searchQuery: query,
      cursorPosition
    };
  }, []);

  // 处理输入变化
  const handleInputChange = useCallback((value: string, cursorPosition: number) => {
    const triggerInfo = detectAtTrigger(value, cursorPosition);
    
    if (triggerInfo) {
      // 触发自动完成
      setAutocompleteState({
        isOpen: true,
        searchQuery: triggerInfo.searchQuery,
        selectedIndex: 0,
        triggerPosition: triggerInfo.triggerPosition,
        cursorPosition: triggerInfo.cursorPosition
      });
    } else if (autocompleteState.isOpen) {
      // 关闭自动完成
      setAutocompleteState(prev => ({
        ...prev,
        isOpen: false
      }));
    }
  }, [autocompleteState.isOpen, setAutocompleteState, detectAtTrigger]);

  // 选择实体
  const selectEntity = useCallback((entity: MixedEntity) => {
    const linkText = `[${entity.title}](tempo://${entity.type}/${entity.id})`;
    
    // 替换文本
    const beforeAt = chatInput.slice(0, autocompleteState.triggerPosition);
    const afterCursor = chatInput.slice(autocompleteState.cursorPosition);
    const newValue = beforeAt + linkText + ' ' + afterCursor;
    
    setChatInput(newValue);
    
    // 关闭自动完成
    setAutocompleteState(prev => ({
      ...prev,
      isOpen: false
    }));

    // 设置光标位置到插入文本后
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPosition = beforeAt.length + linkText.length + 1;
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        textareaRef.current.focus();
      }
    }, 0);
  }, [chatInput, autocompleteState.triggerPosition, autocompleteState.cursorPosition, setChatInput, setAutocompleteState]);

  // 导航选择
  const navigateSelection = useCallback((direction: 'up' | 'down') => {
    if (!autocompleteState.isOpen) return;

    const searchResult = searchEntities(autocompleteState.searchQuery);
    const maxIndex = searchResult.all.length - 1;
    
    setAutocompleteState(prev => ({
      ...prev,
      selectedIndex: direction === 'up' 
        ? Math.max(0, prev.selectedIndex - 1)
        : Math.min(maxIndex, prev.selectedIndex + 1)
    }));
  }, [autocompleteState.isOpen, autocompleteState.searchQuery, searchEntities, setAutocompleteState]);

  // 选择当前项
  const selectCurrentItem = useCallback(() => {
    if (!autocompleteState.isOpen) return;

    const searchResult = searchEntities(autocompleteState.searchQuery);
    const selectedEntity = searchResult.all[autocompleteState.selectedIndex];
    
    if (selectedEntity) {
      selectEntity(selectedEntity);
    }
  }, [autocompleteState.isOpen, autocompleteState.searchQuery, autocompleteState.selectedIndex, searchEntities, selectEntity]);

  // 关闭自动完成
  const closeAutocomplete = useCallback(() => {
    setAutocompleteState(prev => ({
      ...prev,
      isOpen: false
    }));
  }, [setAutocompleteState]);

  // 获取当前搜索结果
  const currentSearchResult = useMemo(() => {
    if (!autocompleteState.isOpen) {
      return { projects: [], tasks: [], all: [] };
    }
    return searchEntities(autocompleteState.searchQuery);
  }, [autocompleteState.isOpen, autocompleteState.searchQuery, searchEntities]);

  return {
    autocompleteState,
    currentSearchResult,
    textareaRef,
    handleInputChange,
    selectEntity,
    navigateSelection,
    selectCurrentItem,
    closeAutocomplete
  };
};
